import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Breadcrumbs from '../navigation/Breadcrumbs';
import { buildServiceBreadcrumbs } from '../navigation/breadcrumbUtils';
import { getBreadcrumbSchema } from '../navigation/breadcrumbSchema';
import SEO from '../seo/SEO';
import './HRInsightsPage.css';

const AnimatedHeading = ({ as: Tag, className, children }) => {
  const text = typeof children === 'string' ? children : '';

  return (
    <Tag className={className} aria-label={text}>
      <span className="insights-heading-text" aria-hidden="true">
        {text.split('').map((char, index) => (
          <span
            key={`${text}-${index}`}
            className={`insights-heading-letter${
              char === ' ' ? ' insights-heading-letter-space' : ''
            }`}
            style={{ '--letter-index': index }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
    </Tag>
  );
};

const HRInsightsPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const servicesIndexView = location.state?.servicesIndexView;
  const backPath =
    servicesIndexView === 'hr' ||
    location.pathname.startsWith('/services/hr/')
      ? '/services/hr'
      : servicesIndexView
        ? '/services'
        : '/';

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const timer = window.setTimeout(() => {
      setIsVisible(true);
    }, 80);

    return () => window.clearTimeout(timer);
  }, []);

  const breadcrumbItems = buildServiceBreadcrumbs('hr', 'HR Insights');
  const breadcrumbSchema = getBreadcrumbSchema(breadcrumbItems);

  return (
    <div className="standard-page-wrapper">
      <SEO schema={breadcrumbSchema} />
      <div className="standard-page-container">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="standard-back-link">
          <Link
            to={backPath}
            state={servicesIndexView ? { servicesIndexView } : undefined}
          >
            {servicesIndexView ? '\u2190 Back' : '\u2190 Back to Home'}
          </Link>
        </div>

        <div className="standard-page-hero">
          <AnimatedHeading
            as="h1"
            className={`insights-main-heading insights-heading-hero${
              isVisible ? ' is-visible' : ''
            }`}
          >
            HR Insights
          </AnimatedHeading>
          <p>
            BlackRoth HR Insights transforms workforce data into meaningful
            intelligence, helping organizations make strategic, data-driven
            people decisions.
          </p>
        </div>

        <section className="standard-page-section">
          <h2>What Are HR Insights</h2>
          <p>
            HR Insights focuses on analyzing workforce data to identify trends,
            risks, and opportunities. Our insights empower leadership teams to
            improve productivity, reduce attrition, and plan future workforce
            needs with confidence.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Key Insight Areas</h2>
          <ul className="standard-service-list">
            <li>Employee Performance & Productivity Metrics</li>
            <li>Attrition & Retention Analysis</li>
            <li>Workforce Cost & Compensation Trends</li>
            <li>Diversity, Equity & Inclusion Reporting</li>
            <li>Absence & Leave Pattern Analysis</li>
          </ul>
        </section>

        <section className="standard-page-section">
          <h2>Advanced Analytics</h2>
          <p>
            Using dashboards and predictive analytics, BlackRoth enables HR and
            leadership teams to proactively address workforce challenges before
            they impact business outcomes.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Business Value</h2>
          <p>
            Our HR Insights help organizations reduce costs, improve employee
            engagement, and align workforce strategies with overall business
            objectives.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Secure & Scalable</h2>
          <p>
            All insights are delivered with enterprise-grade security and
            scalability, ensuring data privacy and compliance across regions
            and industries.
          </p>
        </section>
      </div>
    </div>
  );
};

export default HRInsightsPage;
